import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import Button from '../../../../../../../components/Button';
import RichTextEditor from '../../../../../../../components/HookFormControls/RichTextEditor';
import Typography from '../../../../../../../components/Typography';
import { i18nNS } from '../../../../../../../i18n';
import CloseIcon from '../../../../../../../icons/CloseIcon';
import RichTextIcon from '../../../../../../../icons/RichTextIcon';
import Trash2Icon from '../../../../../../../icons/Trash2Icon';
import { ImageUploadButton } from '../../../../../../../rich-text';
import ImageIcon from '../../../../../../../rich-text/icons/ImageIcon';
import { ChartPalette } from '../../../../../../../styles/chart-palette';
import { Actions, questionOptionClasses, RichTextEditorWrapper } from './styles';
import useQuestionOption, { UseQuestionOption } from './vm';

export interface Props<T extends FieldValues> extends UseQuestionOption<T> {
  label: string;
  color: (typeof ChartPalette)[number];
  placeholder?: string;
  className?: string;
  onChange?: (name: string, value: string) => any;
  onBlur?: () => any;
  canClearRichText: boolean;
  canRemoveOption: boolean;
  onDelete?: () => any;
  /** @default false */
  autoFocus?: boolean;
}

const QuestionOption = <T extends FieldValues>({
  label,
  color,
  placeholder,
  className,
  onChange,
  onBlur,
  canClearRichText,
  canRemoveOption,
  onDelete,
  autoFocus = false,
  ...vmProps
}: Props<T>) => {
  const { t } = useTranslation([i18nNS.POLL]);

  const { control, name } = vmProps;
  const {
    editorRef,
    initialRichText,
    richText,
    handleContentStateChange,
    handleRichTextButtonClick,
    handleBeforeImageUpload,
    handleClearButtonClick,
    selectedMode,
  } = useQuestionOption(vmProps);

  return (
    <Stack direction="row" borderRadius={2} overflow="hidden" className={className}>
      <Stack justifyContent="center" alignItems="center" p={1.25} bgcolor={color}>
        <Typography variant="h6Bold" textTransform="uppercase" color="common.white">
          {label}
        </Typography>
      </Stack>
      <RichTextEditorWrapper flex={1}>
        <RichTextEditor
          editorRef={editorRef}
          control={control}
          onChange={(value) => onChange?.(name, value)}
          onBlur={onBlur}
          inputField={{ name, initialValue: initialRichText }}
          placeholder={placeholder}
          toolbar={
            selectedMode === 'richText'
              ? {
                  canFormatText: true,
                  canInsertFormula: true,
                  canUploadImage: false,
                }
              : false
          }
          onContentStateChange={handleContentStateChange}
          readOnly={selectedMode === 'image'}
          autoFocus={autoFocus}
          className={questionOptionClasses.richTextEditor}
          classes={{
            body: questionOptionClasses.richTextEditorBody,
            errorMessage: questionOptionClasses.richTextEditorErrorMessage,
            toolbar: questionOptionClasses.richTextEditorToolbar,
          }}
        />
        <Actions>
          <Button
            variant={selectedMode === 'richText' ? 'contained' : 'text'}
            size="small"
            startIcon={<RichTextIcon size={18} aria-hidden />}
            onClick={handleRichTextButtonClick}
            className={questionOptionClasses.action}
            classes={{
              startIcon: questionOptionClasses.actionIcon,
            }}
            aria-label={t('click_to_add_rich_text', { ns: i18nNS.POLL })}
          />
          <ImageUploadButton
            editorRef={editorRef}
            isActive={selectedMode === 'image'}
            color="primary"
            size="small"
            startIcon={<ImageIcon size={18} aria-hidden />}
            onBeforeImageUpload={handleBeforeImageUpload}
            classes={{
              toolbarButton: questionOptionClasses.action,
              toolbarIcon: questionOptionClasses.actionIcon,
            }}
            aria-label={t('click_to_upload_image', { ns: i18nNS.POLL })}
          />
          {(canClearRichText || Boolean(richText)) && (
            <Button
              variant="text"
              size="small"
              color="error"
              disabled={!richText}
              startIcon={<CloseIcon size={18} aria-hidden />}
              onClick={handleClearButtonClick}
              className={questionOptionClasses.action}
              classes={{
                startIcon: questionOptionClasses.actionIcon,
              }}
              aria-label={t('click_to_clear_value', { ns: i18nNS.POLL })}
            />
          )}
          {canRemoveOption && !richText && (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={onDelete}
              aria-label={t('click_to_remove_option', { ns: i18nNS.POLL })}
              startIcon={<Trash2Icon size={18} aria-hidden />}
              className={questionOptionClasses.action}
              classes={{
                startIcon: questionOptionClasses.actionIcon,
              }}
            />
          )}
        </Actions>
      </RichTextEditorWrapper>
    </Stack>
  );
};

export default QuestionOption;
